import { Button, notification } from 'antd';
import { FC, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { login } from '@auth';
import { useAccount } from '@hooks';
import { captureException } from '@sentry/react';

/** One reused notification, so repeated failures replace rather than stack. */
const LOGIN_ERROR_KEY = 'keycloak-login-unavailable';

/** Where an already-authenticated visitor to this route belongs. Matches LayoutPublic. */
const POST_LOGIN_ROUTE = '/companies';

/**
 * Not a login screen — Keycloak owns every auth screen, so this app builds no credential form.
 *
 * The route is kept so existing links and the redirect out of the protected layout keep working;
 * landing here hands straight off to the realm.
 */
const Login: FC = () => {
  const { isLogged } = useAccount();

  useEffect(() => {
    // Never hand off with a session already in hand. Keycloak would honour it and bounce straight
    // back here, where this effect fires again — a redirect loop that only ever broke because
    // LayoutPublic's own redirect sometimes won the race.
    if (isLogged) return;

    // The handoff can fail before the browser ever leaves: no realm is configured at all, or
    // keycloak-js cannot build the authorization URL — it needs crypto.subtle for the PKCE challenge,
    // which an insecure origin does not expose, and endpoints that exist only if init reached the
    // realm. Any of those left this page rendering nothing, with no way forward.
    const redirectToRealm = () => {
      login().catch((error) => {
        captureException(error);

        notification.error({
          key: LOGIN_ERROR_KEY,
          message: 'Signing in is unavailable',
          description: 'Signing in is unavailable right now. Please try again.',
          duration: 0,
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                notification.destroy(LOGIN_ERROR_KEY);
                redirectToRealm();
              }}
            >
              Retry
            </Button>
          ),
        });
      });
    };

    redirectToRealm();
  }, [isLogged]);

  // Declared here rather than left to LayoutPublic's effect, so the escape from this route does not
  // depend on which effect runs first.
  if (isLogged) return <Navigate to={POST_LOGIN_ROUTE} replace />;

  return null;
};

export default Login;
